"use client";

import React from "react";
import { ArrowRight, TrendingUp, Globe, ShieldCheck, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const stories = [{
  id: 1,
  client: "AutoTech GmbH",
  category: "Manufacturing Setup",
  title: "Accelerating German Engineering in Pune",
  description: "EuroIndiaTrade facilitated the complete market entry for AutoTech GmbH, from site selection to regulatory compliance, enabling them to start production months ahead of schedule.",
  metricValue: "45%",
  metricLabel: "Reduction in Setup Time",
  icon: Building2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/case_study_manufacturing_pune.png",
  link: "/MarketEntryPage"
}, {
  id: 2,
  client: "Lumière Tech France",
  category: "Buyer Network",
  title: "Expanding Tech Distribution Across India",
  description: "By leveraging our verified buyer database, Lumière Tech quickly identified and partnered with top-tier regional distributors, securing a strong foothold in the Indian market.",
  metricValue: "€2.5M",
  metricLabel: "First Year Pipeline Generated",
  icon: TrendingUp,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/case_study_tech_distribution.png",
  link: "/TradeServicesPage"
}, {
  id: 3,
  client: "Vastra Textiles India",
  category: "Regulatory Compliance",
  title: "Navigating EU Import Regulations Seamlessly",
  description: "Our regulatory experts guided Vastra Textiles through complex EU sustainability and import compliance frameworks, ensuring zero delays at European customs.",
  metricValue: "100%",
  metricLabel: "Customs Clearance Rate",
  icon: ShieldCheck,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/case_study_textile_export.png",
  link: "/RegulatoryPage"
}, {
  id: 4,
  client: "Nordic Freight Solutions",
  category: "Logistics & Freight",
  title: "Optimizing the EU-India Supply Chain",
  description: "We connected Nordic Freight with reliable local logistics partners in India, creating an end-to-end supply chain solution that significantly reduced transit times and costs.",
  metricValue: "30%",
  metricLabel: "Decrease in Logistics Costs",
  icon: Globe,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/case_study_logistics_optimization.png",
  link: "/TradeServicesPage"
}];
export default function StrategicSuccessStories() {
  return <section id="strategic-success-stories" className="dark bg-background text-foreground py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <h2 className="text-base/7 font-semibold text-primary">Proven Results</h2>
              <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl font-serif">
                Strategic Success Stories
              </p>
              <p className="mt-4 text-lg text-muted-foreground">
                Discover how we've helped businesses bridge the gap between European and Indian markets, turning complex cross-border challenges into measurable growth.
              </p>
            </div>
            
            {}
            <div className="hidden md:flex items-center gap-3">
              <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground border-none shadow-md" />
              <CarouselNext className="static translate-y-0 translate-x-0 size-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground border-none shadow-md" />
            </div>
          </div>

          <CarouselContent className="-ml-4 md:-ml-6">
            {stories.map((story, i) => <CarouselItem key={story.id} data-index={i} className="pl-4 md:pl-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 overflow-hidden rounded-3xl border border-border bg-card shadow-2xl transition-all duration-300 hover:shadow-primary/5">
                  
                  {}
                  <div className="relative h-64 sm:h-80 lg:h-full lg:col-span-5 xl:col-span-6">
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent lg:hidden z-10" />
                    <img src={story.image} alt={story.title} className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute bottom-4 left-4 z-20 lg:hidden">
                      <Badge variant="secondary" className="bg-background/90 text-foreground backdrop-blur-sm border-none">
                        {story.category}
                      </Badge>
                    </div>
                  </div>

                  {}
                  <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12 xl:p-16 lg:col-span-7 xl:col-span-6 relative z-20 bg-card">
                    <div className="hidden lg:block mb-6">
                      <Badge variant="secondary" className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-none px-3 py-1 text-sm">
                        {story.category}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{story.client}</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 leading-tight font-serif">
                      {story.title}
                    </h3>
                    
                    <p className="text-muted-foreground text-base sm:text-lg mb-8 leading-relaxed">
                      {story.description}
                    </p>

                    {}
                    <div className="bg-background/50 border border-border rounded-2xl p-6 flex items-center gap-5 mb-8 backdrop-blur-md shadow-sm">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <story.icon className="h-7 w-7" />
                      </div>
                      <div>
                        <div className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                          {story.metricValue}
                        </div>
                        <div className="text-sm sm:text-base font-medium text-muted-foreground mt-1">
                          {story.metricLabel}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto pt-4">
                      <Button variant="default" size="lg" className="w-full sm:w-auto group" asChild>
                        <Link to={story.link}>
                          Read Full Case Study
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex md:hidden items-center justify-center gap-4 mt-8">
            <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground border-none shadow-md" />
            <CarouselNext className="static translate-y-0 translate-x-0 size-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground border-none shadow-md" />
          </div>
        </Carousel>
      </div>
    </section>;
}